import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/about.scss";
import "../css/privacy.scss";

const privacyPrinciples = [
  ["Purpose", "We collect only what we need to provide the service you ask for."],
  ["Clarity", "We explain why information is used in direct, understandable language."],
  ["Control", "You may ask about, correct or delete your personal information where the law allows."],
];

function Privacy() {
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <main className="privacy info-page" id="main-content">
      <div className="info-shell info-shell--narrow">
        <nav className="info-breadcrumb" aria-label="Breadcrumb">
          <Link to="/" onClick={scrollToTop}>Home</Link>
          <span aria-hidden="true">/</span>
          <span aria-current="page">Privacy</span>
        </nav>

        <header className="info-page__header info-page__hero">
          <p className="info-page__eyebrow">Your information</p>
          <h1>Privacy, plainly explained.</h1>
          <p className="info-page__lead">
            This policy describes how {siteConfig.legalName}, trading as
            {` ${siteConfig.brandName}`}, handles personal information when you
            browse, shop or contact us.
          </p>
          <p className="info-page__updated">Last updated · September 2026</p>
        </header>

        <section className="privacy__principles" aria-label="Privacy principles">
          {privacyPrinciples.map(([title, copy], index) => (
            <article key={title}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <h2>{title}</h2>
              <p>{copy}</p>
            </article>
          ))}
        </section>

        <article className="info-page__article">
          <section id="information-we-collect">
            <p className="info-page__eyebrow">01</p>
            <h2>Information we collect</h2>
            <p>
              We may receive contact and delivery details, order information,
              product preferences, customer-care messages and technical data
              about how the storefront is used. Payment credentials are handled
              by the payment service shown at checkout; we do not need your full
              card number to provide customer support.
            </p>
          </section>

          <section id="how-we-use-information">
            <p className="info-page__eyebrow">02</p>
            <h2>How we use it</h2>
            <p>We use personal information only where there is a clear reason, including to:</p>
            <ul>
              <li>process, deliver and support an order;</li>
              <li>answer a question, return request or product-care enquiry;</li>
              <li>protect the storefront and prevent misuse;</li>
              <li>understand performance and improve the shopping experience;</li>
              <li>send marketing only where you have chosen to receive it.</li>
            </ul>
          </section>

          <section id="sharing">
            <p className="info-page__eyebrow">03</p>
            <h2>When information is shared</h2>
            <p>
              We may share the minimum necessary information with service
              partners that help operate the shop—such as payment, delivery,
              hosting, analytics and customer-care providers—or when required
              by law. We do not sell personal information.
            </p>
          </section>

          <section id="cookies">
            <p className="info-page__eyebrow">04</p>
            <h2>Cookies and similar tools</h2>
            <p>
              Essential storage keeps the bag and core site functions working.
              Optional measurement or marketing tools should only be used in
              line with the choices presented to you. Browser settings can also
              be used to remove or block stored data, although some functions
              may then be unavailable.
            </p>
          </section>

          <section id="retention-security">
            <p className="info-page__eyebrow">05</p>
            <h2>Retention and security</h2>
            <p>
              Information is retained only for as long as it is needed for the
              relevant service, legal record-keeping or dispute resolution. We
              use proportionate organisational and technical safeguards, while
              recognising that no online service can promise absolute security.
            </p>
          </section>

          <section id="rights">
            <p className="info-page__eyebrow">06</p>
            <h2>Your choices and rights</h2>
            <p>
              Depending on where you live, you may have rights to access,
              correct, erase, restrict or receive a copy of your information,
              and to object to certain uses. You may withdraw marketing consent
              at any time. Identity verification may be required before a
              request can be completed.
            </p>
          </section>

          <section id="lawful-bases">
            <p className="info-page__eyebrow">07</p>
            <h2>Reasons permitted by law</h2>
            <p>
              Where applicable, we rely on the reason appropriate to the use:
              performing an order or requested service, meeting a legal duty,
              a legitimate operational interest that does not override your
              rights, or consent where it is required.
            </p>
          </section>

          <section id="international-transfers">
            <p className="info-page__eyebrow">08</p>
            <h2>International transfers</h2>
            <p>
              A service provider may process information outside your country.
              Where transfer safeguards are required, we expect the provider to
              use an available lawful mechanism and appropriate protections.
            </p>
          </section>

          <section id="children-and-complaints">
            <p className="info-page__eyebrow">09</p>
            <h2>Children and complaints</h2>
            <p>
              This storefront is not directed to children who cannot lawfully
              place an order. If you have a privacy concern, contact us first so
              we can review it. You may also have the right to complain to the
              data-protection authority where you live.
            </p>
          </section>
        </article>

        <section className="info-faq privacy__faq" aria-labelledby="privacy-faq-title">
          <div>
            <p className="info-page__eyebrow">Questions</p>
            <h2 id="privacy-faq-title">Your data, your choice.</h2>
          </div>
          <div className="info-faq__list">
            <details>
              <summary>How can I make a privacy request?</summary>
              <p>Email us from the address connected to your order and describe the request. We will explain any verification needed.</p>
            </details>
            <details>
              <summary>Can I stop marketing messages?</summary>
              <p>Yes. Use the unsubscribe option in a message or contact our team. Service messages about an active order may still be sent.</p>
            </details>
            <details>
              <summary>Where can I read about cookies?</summary>
              <p>Our <Link to="/cookies" onClick={scrollToTop}>cookie policy</Link> explains necessary storage, optional tools and browser controls.</p>
            </details>
          </div>
        </section>

        <aside className="info-page__contact-card">
          <p className="info-page__eyebrow">Privacy contact</p>
          <h2>Want to ask about your information?</h2>
          <p>
            The data controller is {siteConfig.legalName}, {siteConfig.contact.address}.
            Contact us at <a href={`mailto:${siteConfig.contact.email}`}>{siteConfig.contact.email}</a>.
          </p>
        </aside>
      </div>
    </main>
  );
}

export default Privacy;
